import { Modal } from "antd";
import http from "@/api/http";
import api from "@/api";
import { getServiceVal, setServiceVal } from "@/utils/cookies";
import { authCookieConfig, DESKTOP_X86_DOWNLOAD_SECTION_ID, FREE_DOWNLOAD_PATH, loginUrl, X86_OBTAIN_PAGE_PATH, X86_PAGE_PATH } from "@/utils/base";
import dataJson from "@/../public/data.json";
import {
	findDesktopPackageByDownloadKey,
	findDesktopPackageByLinkKey,
	getAllDesktopDownloadGroups,
} from "./desktopDownloadPackage";

/** POST `/community-api/community/developerX86ImageDownload/save` 的请求体（与后端约定一致） */
export type DeveloperX86SaveBody = {
	/** 登录回流仅绑意向时可不传；用户再次点击下载时传完整参数 */
	packageTitle?: string;
	versionNo?: string;
	fileSize?: string;
	releaseDate?: string;
	summary?: string;
	changelog?: string;
	/** 注册回流 download 页时携带，绑定下载意向 */
	intentId?: string;
	/** 可选；后端以登录态为准，前端仅在 save 时按需附带 */
	userId?: string;
};

/** POST `/community/downloadIntent/bind`（需登录） */
export type DownloadIntentBindBody = {
	intentId: string;
	/** 用户注册时间戳（毫秒），来自 accountUser.createTime */
	userCreateTime?: number;
};

/** POST `/community/downloadIntent/create`（免登录） */
export type DownloadIntentCreateBody = {
	intentId: string;
	packageTitle?: string;
	versionNo?: string;
	fileSize?: string;
	releaseDate?: string;
	summary?: string;
	changelog?: string;
};

/** sessionStorage：与后端 li_developer_download_intent.intent_id 一致 */
export const X86_DOWNLOAD_INTENT_ID_KEY = "x86_download_intent";

/** sessionStorage：回流 save 时还原对应镜像包 */
export const X86_DOWNLOAD_INTENT_RESOURCE_KEY = "x86_download_intent_resource";

let bindIntentInFlight: Promise<boolean> | null = null;

const X86_INTENT_COOKIE_NAME = "x86_intent";

/** Cookie 兜底：回流页 sessionStorage 丢失时仍能还原镜像包 key */
const X86_INTENT_RESOURCE_COOKIE_NAME = "x86_intent_resource";

const X86_INTENT_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 7;

/**
 * 仅登记 X86 桌面系统镜像下载（与接口 developerX86ImageDownload 语义一致）。
 *
 * 与「KaihongOS桌面版 → 软件包」截图七卡对应关系：
 * - 会调本接口：X86 5.0 试用版、QEMU 试用版、开发者问题验证版 的「立即下载」；另 + X86 详情页「立即下载」（同试用版 key）。
 * - 不会调：① RK3588、② 二开包（申请获取，无 handleBtn 下载）、④ Arm64 小龙虾、⑤ AI 工具、⑥ DeepSeek（弹窗内选 1.5B/7B 后下载）。
 * 新增 X86 系统镜像时把 data.json 中对应 `download` key 加入集合。
 */
const DEVELOPER_X86_IMAGE_DOWNLOAD_KEYS = new Set<string>([
	"image_kaihong_desktop_x86_v500",
	"image_kaihong_desktop_x86_qemu_v500",
	"image_kaihong_desktop_x86_verified",
]);

export function shouldReportDesktopDownloadAttraction(originalKey: string): boolean {
	const k = (originalKey || "").trim();
	if (!k || k.includes("https://") || k.includes("http://")) {
		return false;
	}
	return DEVELOPER_X86_IMAGE_DOWNLOAD_KEYS.has(k);
}

function stripHtml(s: string): string {
	return s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

/** 从卡片 describe 纯文本里尝试抠出版本号（无 package_meta 时的兜底） */
function extractVersionFromDescribe(desc?: string): string | null {
	if (!desc) return null;
	const plain = stripHtml(desc);
	const m = plain.match(/版本号[：:]\s*([^\s]+)/);
	return m?.[1] ?? null;
}

/** Powered by OpenHarmony：5.0.1.41 等 */
function extractVersionOpenHarmonyLine(desc?: string): string | null {
	if (!desc) return null;
	const plain = stripHtml(desc);
	const m = plain.match(/OpenHarmony[：:]\s*([\d.]+)/i);
	return m?.[1] ?? null;
}

/** 无 package_meta / 无「版本号」文案时，从标题或描述里取 x.y 或 x.y.z（如验证版卡片） */
function extractVersionLoose(s?: string): string | null {
	if (!s) return null;
	const m = String(s).match(/\d+\.\d+(?:\.\d+)?/);
	return m?.[0] ?? null;
}

/**
 * 根据桌面专区配置的 download / link key 组装上报 JSON（必填 packageTitle、versionNo）。
 */
export function buildDeveloperX86SaveBody(resourceKey: string): DeveloperX86SaveBody {
	const desktop = getAllDesktopDownloadGroups();

	const item =
		findDesktopPackageByDownloadKey(desktop, resourceKey) ??
		findDesktopPackageByLinkKey(desktop, resourceKey);

	if (item != null) {
		const title =
			[item.title, item.sub_title].filter(Boolean).join(" ").trim() ||
			resourceKey;
		const meta = item.package_meta;
		const descPlain = item.describe ? stripHtml(item.describe) : "";
		const versionNo =
			meta?.version?.trim() ||
			extractVersionFromDescribe(item.describe) ||
			extractVersionOpenHarmonyLine(item.describe) ||
			extractVersionLoose(title) ||
			extractVersionLoose(descPlain) ||
			"-";

		const body: DeveloperX86SaveBody = {
			packageTitle: title,
			versionNo,
		};
		if (meta?.size) body.fileSize = meta.size;
		if (meta?.release_date) body.releaseDate = meta.release_date;
		const summaryPlain = item.describe ? stripHtml(item.describe) : "";
		if (summaryPlain) body.summary = summaryPlain.slice(0, 4000);
		if (meta?.update_notes) body.changelog = meta.update_notes;
		return body;
	}

	return {
		packageTitle: resourceKey,
		versionNo: "-",
	};
}

export function newX86DownloadIntentId(): string {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}
	return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getX86IntentCookieDomain(): string | undefined {
	if (typeof window === "undefined") {
		return undefined;
	}
	const host = window.location.hostname;
	if (
		host === "localhost" ||
		host === "127.0.0.1" ||
		/^\d+\.\d+\.\d+\.\d+$/.test(host)
	) {
		return undefined;
	}
	if (host === "kaihong.com" || host.endsWith(".kaihong.com")) {
		return ".kaihong.com";
	}
	return undefined;
}

export function setX86DownloadIntentStorage(
	intentId: string,
	resourceKey: string,
): void {
	try {
		sessionStorage.setItem(X86_DOWNLOAD_INTENT_ID_KEY, intentId);
		sessionStorage.setItem(X86_DOWNLOAD_INTENT_RESOURCE_KEY, resourceKey);
	} catch {
		/* ignore */
	}
	const domain = getX86IntentCookieDomain();
	const cookieBase = `;path=/;max-age=${X86_INTENT_COOKIE_MAX_AGE_SEC};SameSite=Lax`;
	const domainPart = domain ? `;domain=${domain}` : "";
	document.cookie = `${X86_INTENT_COOKIE_NAME}=${encodeURIComponent(intentId)}${cookieBase}${domainPart}`;
	document.cookie = `${X86_INTENT_RESOURCE_COOKIE_NAME}=${encodeURIComponent(resourceKey)}${cookieBase}${domainPart}`;
}

export function getX86DownloadIntentId(): string | null {
	try {
		const fromSession = sessionStorage.getItem(X86_DOWNLOAD_INTENT_ID_KEY);
		if (fromSession) {
			return fromSession;
		}
	} catch {
		/* ignore */
	}
	const matched = document.cookie.match(
		new RegExp(`(?:^|;\\s*)${X86_INTENT_COOKIE_NAME}=([^;]+)`),
	);
	return matched ? decodeURIComponent(matched[1]) : null;
}

export function getX86DownloadIntentResourceKey(): string | null {
	try {
		const fromSession = sessionStorage.getItem(
			X86_DOWNLOAD_INTENT_RESOURCE_KEY,
		);
		if (fromSession) {
			return fromSession;
		}
	} catch {
		/* ignore */
	}
	const matched = document.cookie.match(
		new RegExp(
			`(?:^|;\\s*)${X86_INTENT_RESOURCE_COOKIE_NAME}=([^;]+)`,
		),
	);
	return matched ? decodeURIComponent(matched[1]) : null;
}

export function clearX86DownloadIntentStorage(): void {
	try {
		sessionStorage.removeItem(X86_DOWNLOAD_INTENT_ID_KEY);
		sessionStorage.removeItem(X86_DOWNLOAD_INTENT_RESOURCE_KEY);
	} catch {
		/* ignore */
	}
	const domain = getX86IntentCookieDomain();
	const expiredBase = ";path=/;max-age=0;SameSite=Lax";
	const domainPart = domain ? `;domain=${domain}` : "";
	document.cookie = `${X86_INTENT_COOKIE_NAME}=${expiredBase}${domainPart}`;
	document.cookie = `${X86_INTENT_RESOURCE_COOKIE_NAME}=${expiredBase}${domainPart}`;
}

async function enrichDeveloperX86SaveBody(
	body: DeveloperX86SaveBody,
	explicitUserId?: string,
): Promise<DeveloperX86SaveBody> {
	const next = { ...body };
	if (next.userId) {
		return next;
	}
	if (explicitUserId) {
		next.userId = explicitUserId;
		return next;
	}
	const userId = await resolveCurrentUserId();
	if (userId) {
		next.userId = userId;
	}
	return next;
}

/** 已登录点击下载 / 回流确认时组装 save 请求体 */
export function buildX86ImageDownloadSavePayload(
	resourceKey: string,
	options?: { intentId?: string | null; userId?: string },
): DeveloperX86SaveBody {
	const payload: DeveloperX86SaveBody = {
		...buildDeveloperX86SaveBody(resourceKey),
	};
	const intentId = options?.intentId ?? getX86DownloadIntentId();
	if (intentId) {
		payload.intentId = intentId;
	}
	if (options?.userId) {
		payload.userId = options.userId;
	}
	return payload;
}

function toDownloadIntentCreateBody(
	intentId: string,
	card: DeveloperX86SaveBody,
): DownloadIntentCreateBody {
	return {
		intentId,
		packageTitle: card.packageTitle,
		versionNo: card.versionNo,
		fileSize: card.fileSize,
		releaseDate: card.releaseDate,
		summary: card.summary,
		changelog: card.changelog,
	};
}

/**
 * 未登录点击 X86 镜像下载：生成 intentId、写入 storage/cookie，并创建下载意向。
 */
export async function createDownloadIntentWithRetry(
	body: DownloadIntentCreateBody,
	maxAttempts = 3,
): Promise<boolean> {
	const url = "/community-api/community/downloadIntent/create";
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			const res: any = await http.post(url, body, {
				headers: { "Content-Type": "application/json" },
			});
			if (res?.code === 200) {
				return true;
			}
			console.warn("[downloadIntent/create] unexpected response:", res);
		} catch (e) {
			console.error(
				`[downloadIntent/create] attempt ${attempt} failed`,
				e,
			);
		}
		if (attempt < maxAttempts) {
			await new Promise((r) => setTimeout(r, 350 * attempt));
		}
	}
	return false;
}

/**
 * 未登录点「立即下载」→ create 意向（PENDING）；跳转登录。
 * 在 X86 详情页：登录回流仍回到 X86 页（bind 意向）；在资源下载页：回流至 X86 板块。
 * 再次点击下载 → save 完整参数 + 可选 intentId（补充 x86 下载明细）。
 */
export async function beginX86DeveloperDownloadLoginFlow(
	resourceKey: string,
): Promise<void> {
	markX86DeveloperLoginIntent();
	if (!shouldReportDesktopDownloadAttraction(resourceKey)) {
		return;
	}
	const intentId = newX86DownloadIntentId();
	const card = buildDeveloperX86SaveBody(resourceKey);
	setX86DownloadIntentStorage(intentId, resourceKey);
	await createDownloadIntentWithRetry(toDownloadIntentCreateBody(intentId, card));
}

/**
 * 外部注册/登录回流常在 URL 带 `?token=`，写入 Authorization 后供 save 识别用户。
 * @returns 解析到的 token，无则 null
 */
export function consumeAuthorizationTokenFromUrl(): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	const params = new URLSearchParams(window.location.search);
	const token = params.get("token")?.trim();
	if (!token) {
		return null;
	}
	setServiceVal("Authorization", token, {
		expires: authCookieConfig.expires,
		path: authCookieConfig.path,
		domain: authCookieConfig.domain,
	});
	params.delete("token");
	const qs = params.toString();
	const next = `${window.location.pathname}${window.location.hash}${qs ? `?${qs}` : ""}`;
	window.history.replaceState(null, "", next);
	return token;
}

export type SaveX86ImageDownloadOptions = {
	explicitUserId?: string;
	maxAttempts?: number;
	/** 默认 true：用户点击下载成功后清除 intent；登录仅绑意向时为 false */
	clearIntentOnSuccess?: boolean;
};

/** save 日限（与后端 community-api 约定一致） */
export const X86_IMAGE_DOWNLOAD_DAILY_LIMIT_CODE = 1018;

export type X86ImageDownloadSaveResult = {
	ok: boolean;
	code?: number;
	message: string;
};

function pickApiMessage(res: any): string {
	const text = res?.message ?? res?.msg;
	return typeof text === "string" ? text.trim() : "";
}

function normalizeApiCode(raw: unknown): number | undefined {
	if (typeof raw === "number" && Number.isFinite(raw)) {
		return raw;
	}
	if (typeof raw === "string" && raw.trim() !== "") {
		const n = Number.parseInt(raw, 10);
		if (Number.isFinite(n)) {
			return n;
		}
	}
	return undefined;
}

const SAVE_POST_HTTP_CONFIG = {
	headers: { "Content-Type": "application/json" },
	/** 400 仍走 success，body 含 code/message，避免 axios 抛错后丢失业务文案 */
	validateStatus: (status: number) => status >= 200 && status < 600,
};

function parseSaveApiBody(
	res: unknown,
	options?: { clearIntentOnSuccess?: boolean; intentId?: string },
): X86ImageDownloadSaveResult | null {
	if (!res || typeof res !== "object") {
		return null;
	}
	const code = normalizeApiCode((res as { code?: unknown }).code);
	const message = pickApiMessage(res);
	if (code === 200) {
		if (options?.intentId && options?.clearIntentOnSuccess) {
			clearX86DownloadIntentStorage();
		}
		return { ok: true, code: 200, message: message || "操作成功" };
	}
	if (code === X86_IMAGE_DOWNLOAD_DAILY_LIMIT_CODE) {
		return {
			ok: false,
			code: X86_IMAGE_DOWNLOAD_DAILY_LIMIT_CODE,
			message: message || "下载次数已达上限，请稍后再试",
		};
	}
	if (code === 1019) {
		if (options?.intentId && options?.clearIntentOnSuccess) {
			clearX86DownloadIntentStorage();
		}
		return { ok: true, code: 1019, message: message || "操作成功" };
	}
	if (code != null) {
		return { ok: false, code, message: message || "请求失败" };
	}
	return message ? { ok: false, message } : null;
}

/** HTTP 4xx 且未设 validateStatus 时，业务 body 在 err.response.data */
function extractSaveResultFromHttpError(
	e: unknown,
): X86ImageDownloadSaveResult | null {
	const err = e as {
		response?: { data?: unknown };
	};
	const data = err?.response?.data;
	if (!data || typeof data !== "object") {
		return null;
	}
	return parseSaveApiBody(data);
}

/** X86 下载 save 后：对话框展示后端 message，用户点击「确认」后才继续后续流程 */
export function showX86DownloadQuotaModal(
	messageText: string,
	code?: number,
): Promise<void> {
	const content =
		messageText.trim() !== "" ? messageText : "操作成功";
	const isLimit = code === X86_IMAGE_DOWNLOAD_DAILY_LIMIT_CODE;

	return new Promise((resolve) => {
		const openModal = isLimit ? Modal.warning : Modal.info;
		openModal({
			title: "温馨提示",
			content,
			okText: "确认",
			centered: true,
			maskClosable: false,
			onOk: () => {
				resolve();
			},
		});
	});
}

export async function resolveCurrentUserId(): Promise<string | null> {
	if (!getServiceVal("Authorization")) {
		return null;
	}
	try {
		const res: any = await api.getUserDetail();
		if (res?.code === 200 && res?.data) {
			const id =
				res.data.userId ?? res.data.user_id ?? res.data.id;
			if (id != null && String(id).trim() !== "") {
				return String(id);
			}
		}
	} catch (e) {
		console.warn("[resolveCurrentUserId] getUserDetail failed", e);
	}
	return null;
}

function parseUserCreateTime(
	data: Record<string, unknown> | null | undefined,
): number | undefined {
	if (!data) {
		return undefined;
	}
	const raw = data.createTime ?? data.create_time;
	if (raw == null || raw === "") {
		return undefined;
	}
	const n = Number(raw);
	return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** 登录用户注册时间（毫秒），用于 downloadIntent/bind */
export async function resolveUserCreateTime(): Promise<number | undefined> {
	if (!getServiceVal("Authorization")) {
		return undefined;
	}
	try {
		const res: any = await api.getUserDetail();
		if (res?.code === 200 && res?.data) {
			return parseUserCreateTime(res.data);
		}
	} catch (e) {
		console.warn("[resolveUserCreateTime] getUserDetail failed", e);
	}
	return undefined;
}

/**
 * POST `/community/downloadIntent/bind`：登录态下将意向标为 CONVERTED。
 */
export async function bindDownloadIntentWithRetry(
	intentId: string,
	maxAttempts = 3,
	options?: { userCreateTime?: number },
): Promise<boolean> {
	const url = "/community-api/community/downloadIntent/bind";
	let userCreateTime = options?.userCreateTime;
	if (userCreateTime == null) {
		userCreateTime = await resolveUserCreateTime();
	}
	const body: DownloadIntentBindBody = { intentId };
	if (userCreateTime != null) {
		body.userCreateTime = userCreateTime;
	}
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			const res: any = await http.post(url, body, {
				headers: { "Content-Type": "application/json" },
			});
			if (res?.code === 200) {
				return true;
			}
			if (res?.code === 1019) {
				console.warn(
					"[downloadIntent/bind] intent bound by another user:",
					res,
				);
				return false;
			}
			console.warn("[downloadIntent/bind] unexpected response:", res);
		} catch (e) {
			console.error(
				`[downloadIntent/bind] attempt ${attempt} failed`,
				e,
			);
		}
		if (attempt < maxAttempts) {
			await new Promise((r) => setTimeout(r, 350 * attempt));
		}
	}
	return false;
}

/**
 * 注册/登录回到 download（或 X86）页：已登录则 bind，完成归因；不调 save。
 */
export async function bindX86DownloadIntentAfterLogin(): Promise<boolean> {
	if (bindIntentInFlight) {
		return bindIntentInFlight;
	}

	consumeAuthorizationTokenFromUrl();

	const intentId = getX86DownloadIntentId();
	if (!intentId || !getServiceVal("Authorization")) {
		return false;
	}

	bindIntentInFlight = (async () => {
		try {
			const ok = await bindDownloadIntentWithRetry(intentId);
			if (ok) {
				clearX86DownloadIntentStorage();
			}
			return ok;
		} finally {
			bindIntentInFlight = null;
		}
	})();

	return bindIntentInFlight;
}

/**
 * 登记「由桌面版/X86 下载入口吸引」；需登录态；POST JSON Body；失败自动重试。
 * 用户再次点击「立即下载」时传完整镜像参数；若仍有 intentId 则一并带上。
 */
export async function saveDeveloperX86ImageDownloadWithRetry(
	body: DeveloperX86SaveBody,
	options?: SaveX86ImageDownloadOptions,
): Promise<X86ImageDownloadSaveResult> {
	const {
		explicitUserId,
		maxAttempts = 3,
		clearIntentOnSuccess = true,
	} = options ?? {};

	if (!getServiceVal("Authorization")) {
		console.warn(
			"[developerX86ImageDownload/save] skipped: no Authorization cookie",
		);
		return { ok: false, message: "请先登录后再下载" };
	}
	const payload = await enrichDeveloperX86SaveBody(body, explicitUserId);
	const url = "/community-api/community/developerX86ImageDownload/save";
	let lastMessage = "";
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			const res: unknown = await http.post(url, payload, SAVE_POST_HTTP_CONFIG);
			lastMessage = pickApiMessage(res);
			const parsed = parseSaveApiBody(res, {
				intentId: payload.intentId,
				clearIntentOnSuccess,
			});
			if (parsed) {
				return parsed;
			}
			console.warn(
				"[developerX86ImageDownload/save] unexpected response:",
				res,
			);
		} catch (e) {
			const fromHttp = extractSaveResultFromHttpError(e);
			if (fromHttp) {
				return fromHttp;
			}
			console.error(
				`[developerX86ImageDownload/save] attempt ${attempt} failed`,
				e,
			);
		}
		if (attempt < maxAttempts) {
			await new Promise((r) => setTimeout(r, 350 * attempt));
		}
	}
	return {
		ok: false,
		message: lastMessage || "网络异常，请稍后重试",
	};
}

/** 登录页 query：X86 桌面版下载引流注册统计 */
export const X86_DEVELOPER_SCOPE = "developers_x86";

/** 用户点击 X86「立即下载」后写入；等待提示期间点右上角登录也走带 scope 的地址 */
export const X86_DEVELOPER_LOGIN_INTENT_KEY = "developers_x86_login_intent";

export function isX86PageUrl(href: string): boolean {
	try {
		const { pathname } = new URL(href, window.location.origin);
		const normalized = pathname.replace(/\/$/, "").toLowerCase();
		return (
			normalized.endsWith("/x86") ||
			normalized.endsWith("/x86/obtain") ||
			normalized.endsWith("/pages/x86/x86") ||
			pathname.endsWith(X86_PAGE_PATH) ||
			pathname.endsWith(X86_OBTAIN_PAGE_PATH)
		);
	} catch {
		const lower = href.toLowerCase();
		return (
			lower.includes("/x86") ||
			lower.includes("/pages/x86/x86") ||
			href.includes(X86_PAGE_PATH) ||
			href.includes(X86_OBTAIN_PAGE_PATH)
		);
	}
}

/** 登录回流 jump_url：去掉 hash，保留 pathname + search */
export function normalizeLoginJumpUrl(href: string): string {
	try {
		const u = new URL(href, window.location.origin);
		return `${u.origin}${u.pathname}${u.search}`;
	} catch {
		return href;
	}
}

export function markX86DeveloperLoginIntent(): void {
	try {
		sessionStorage.setItem(X86_DEVELOPER_LOGIN_INTENT_KEY, "1");
	} catch {
		/* sessionStorage 不可用时忽略 */
	}
}

export function clearX86DeveloperLoginIntent(): void {
	try {
		sessionStorage.removeItem(X86_DEVELOPER_LOGIN_INTENT_KEY);
	} catch {
		/* ignore */
	}
}

export function hasX86DeveloperLoginIntent(): boolean {
	try {
		return sessionStorage.getItem(X86_DEVELOPER_LOGIN_INTENT_KEY) === "1";
	} catch {
		return false;
	}
}

export function getX86DeveloperLoginJumpUrl(): string {
	return `${window.location.origin}${FREE_DOWNLOAD_PATH}#${DESKTOP_X86_DOWNLOAD_SECTION_ID}`;
}

/** 场景1：X86 详情页点「立即下载」→ 登录后回到 X86 页 */
export function buildX86PageDownloadLoginUrl(
	returnUrl: string = typeof window !== "undefined"
		? window.location.href
		: "",
): string {
	const jump = normalizeLoginJumpUrl(returnUrl || `${window.location.origin}${X86_PAGE_PATH}`);
	return `${loginUrl}?jump_url=${jump}&scope=${X86_DEVELOPER_SCOPE}`;
}

/** 场景2：X86 页右上角登录 / 资源下载页 X86 卡片 → 登录后回到下载页 X86 板块 */
export function buildX86DeveloperLoginUrl(): string {
	const jump = getX86DeveloperLoginJumpUrl();
	return `${loginUrl}?jump_url=${jump}&scope=${X86_DEVELOPER_SCOPE}`;
}

/** 统一解析登录地址 */
export function resolveLoginUrl(
	jumpHref: string = typeof window !== "undefined" ? window.location.href : "",
): string {
	const onX86Page = isX86PageUrl(jumpHref);
	const fromDownloadFlow =
		hasX86DeveloperLoginIntent() && Boolean(getX86DownloadIntentId());

	// X86 页已点「立即下载」：回流 X86 页（含等待提示期间改点右上角登录）
	if (onX86Page && fromDownloadFlow) {
		return buildX86PageDownloadLoginUrl(jumpHref);
	}
	// X86 页右上角登录：回流资源下载页 X86 板块
	if (onX86Page) {
		return buildX86DeveloperLoginUrl();
	}
	if (hasX86DeveloperLoginIntent()) {
		return buildX86DeveloperLoginUrl();
	}
	return `${loginUrl}?jump_url=${jumpHref}`;
}
